import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom";

import { RootForegroundNotificiationController } from "helpers/pushNotification.utilities";

import "utilities/api/setupApix";
// Intentionally adding this here so storybook doesn't load. That way if everything works in storybook, we can just delete this when webforms is gone (in 2050)
import { getAppDefaultFlagValue, getAppDefaultFlagValueAsBoolean } from "utilities/appDefaultFlags";
import {
    getCurrentEnvironment,
    getCurrentSentryEnvironment,
} from "utilities/environment/environment";
import { lazyLoadWithRetry } from "utilities/react.utilities";
import "utilities/webview/setupMobileMock";

import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import { registerDragAndDropPlugins } from "commonComponents/utilities/DragAndDrop/DragAndDrop";
import { RootFocusObserver } from "commonComponents/utilities/Focus/RootFocusObserver";

import "./styles/webformsCssHacks.less";

import { App } from "./App";

const BTAdminWebformsMenu = lazyLoadWithRetry(() => import("./layouts/BTAdminWebformsMenu"));

window.rootFocusObserver = new RootFocusObserver();
window.rootForegroundNotificationController = new RootForegroundNotificiationController();

const environment: string = getCurrentEnvironment();

const enableRUM = getAppDefaultFlagValueAsBoolean("dataDogRUM");
const enableTracing = getAppDefaultFlagValueAsBoolean("dataDogEnableTracing");
const ddAppId = getAppDefaultFlagValue("dataDogApplicationId");
const ddToken = getAppDefaultFlagValue("dataDogClientToken");

// Benign browser errors that aren't actionable app bugs — filtered from both
// RUM and Sentry to reduce noise. NetworkError is broad so only Sentry ignores it.
const commonIgnoredErrors = [
    "ResizeObserver loop",
    // Salesforce ESW chat bundle: deferred callbacks reference an
    // `embeddedservice_bootstrap` global that is gone by the time the
    // user has logged out/navigated away. The SDK is minified and
    // can't be patched.
    "embeddedservice_bootstrap",
    // Microsoft Word saves documents as HTML with onclick handlers that call
    // msoCommentHide / msoCommentShow. When Word-generated HTML is rendered
    // in BT (e.g. rich text fields), clicking comment annotations fires these
    // ReferenceErrors. The functions are Office-internal and can't be stubbed.
    "msoComment",
];

if (enableRUM && ddAppId && ddToken) {
    datadogRum.init({
        applicationId: ddAppId,
        clientToken: ddToken,
        site: getAppDefaultFlagValue("dataDogSite"),
        service: "buildertrend.net",
        env: environment,
        sessionSampleRate: getAppDefaultFlagValue("dataDogSessionSampleRate"),
        sessionReplaySampleRate: getAppDefaultFlagValue("dataDogPremiumSampleRate"), // if not included, the default is 100
        trackUserInteractions: getAppDefaultFlagValueAsBoolean("dataDogTrackUserInteractions"),
        trackResources: true,
        trackLongTasks: true,
        allowedTracingUrls: enableTracing ? [/https:\/\/.*buildertrend\.net/] : undefined,
        defaultPrivacyLevel: "mask-user-input",
        version: window.btJScriptGlobals.sentryRelease,
        enableExperimentalFeatures: ["feature_flags"],
        beforeSend: (event) => {
            if (
                event.type === "error" &&
                commonIgnoredErrors.some((msg) => event.error?.message?.includes(msg))
            ) {
                return false;
            }
            return true;
        },
    });
    datadogRum.startSessionReplayRecording();
}

const dsnVal = window.btJScriptGlobals.appDefaultFlags.sentryReactDSN;
const enableSentry = getAppDefaultFlagValueAsBoolean("enableSentry");
const sentryEnvironment: string = getCurrentSentryEnvironment();

if (dsnVal && enableSentry) {
    const tracesSampleRateString = window.btJScriptGlobals.appDefaultFlags
        .sentryReactSamplingRate as string;
    const tracesSampleRate = parseFloat(tracesSampleRateString) || 0;

    const integrations: any[] = [
        Sentry.browserTracingIntegration({
            beforeStartSpan: (context) => {
                context.name = location.pathname
                    .replace(/\/[a-f0-9]{32}/g, "/<hash>")
                    .replace(/\/\d+/g, "/<digits>");
                return context;
            },
        }),
        Sentry.httpClientIntegration(),
    ];

    Sentry.init({
        dsn: dsnVal,
        environment: sentryEnvironment,
        ignoreErrors: [...commonIgnoredErrors, "NetworkError"],
        denyUrls: [/maps\.googleapis\.com\/maps-api-v3\//, /\/ESWBuildertrendChat\d+\//],
        integrations: integrations,
        tracesSampleRate: tracesSampleRate,
        beforeBreadcrumb: (breadcrumb, hint) => {
            if (breadcrumb.category === "ui.click" && hint && hint.event) {
                const { target } = hint.event;
                if (target && target.dataset && target.dataset.testid) {
                    breadcrumb.message += ` data-testid['${target.dataset.testid}']`;
                }
            }

            return breadcrumb;
        },
        release: window.btJScriptGlobals.sentryRelease,
    });
}

// Register SortableJS Plugins
registerDragAndDropPlugins();

// path starts with /app/
const isSinglePageApp = window.location.pathname.toLowerCase().startsWith("/app");
const reactSinglePageAppRoot = document.getElementById("root");

// Please do not add any more code to this file, do full react conversion instead.

// Start List Page Rendering

// TODO: Remove all instances of useStickyHeaderWithMainNav(); once we are on SPA.

const divReactHeaderMenu = document.getElementById("divReactHeaderMenu");
if (divReactHeaderMenu) {
    ReactDOM.render(
        <BTSuspense>
            <BTAdminWebformsMenu />
        </BTSuspense>,
        divReactHeaderMenu,
    );
}

// Please do not add any more code to this file, do full react conversion instead.

// End List Page Rendering

if (isSinglePageApp && reactSinglePageAppRoot) {
    ReactDOM.render(<App />, reactSinglePageAppRoot);
}
